import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;

const LanguageList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} languages`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    searchText: ''
  });

  const getLanguages = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.searchText,
        ...params
      };
      
      console.log('Fetching languages with params:', requestParams);
      const response = await masterService.getVideoLanguages(requestParams);
      console.log('Languages API Response:', response);
      
      if (response.data && response.data.status) {
        // Handle different possible response structures
        const languagesData = response.data.data?.data || response.data.data || [];
        const paginationData = response.data.data?.pagination || response.data.pagination || {};
        
        console.log('Languages data:', languagesData);
        console.log('Pagination data:', paginationData);
        
        // Ensure languagesData is an array
        if (Array.isArray(languagesData)) {
          setLanguages(languagesData);
        } else {
          console.error('Languages data is not an array:', languagesData);
          setLanguages([]);
        }
        
        setPagination(prev => ({
          ...prev,
          ...paginationData,
          current: paginationData.currentPage || 1
        }));
      } else {
        message.error('Failed to fetch languages');
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      message.error('Failed to fetch languages');
      setLanguages([]);
    } finally {
      setLoading(false);
    }
  }, [filters.searchText]);

  // Load languages data
  useEffect(() => {
    getLanguages();
  }, [getLanguages]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getLanguages({
      searchText: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getLanguages({
      searchText: filters.searchText,
      perPage: pageSize,
      currentPage: current
    });
  };

  // Handle add language
  const handleAddLanguage = () => {
    navigate('/master/addvideolanguage');
  };

  // Handle edit language
  const handleEditLanguage = (langId) => {
    navigate(`/master/editvideolanguage/${langId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Language Name',
      dataIndex: 'lang_name',
      key: 'lang_name',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.lang_name.localeCompare(b.lang_name),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'ISO Code',
      dataIndex: 'lang_iso_639_2_code',
      key: 'lang_iso_639_2_code',
      width: 120,
      render: (text) => (
        <Tag color="blue" style={{ fontSize: '12px' }}>
          {text}
        </Tag>
      )
    },
    {
      title: 'Native Name',
      dataIndex: 'native_name',
      key: 'native_name',
      render: (text) => (
        <Text style={{ fontSize: '13px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'lang_is_active',
      key: 'lang_is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditLanguage(record.lang_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Video Languages</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Video Language List"
      desc="Manage video languages and their status"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Video Languages ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddLanguage}
              size="large"
            >
              Add Language
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search languages..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={languages}
          rowKey="lang_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
          locale={{ 
            emptyText: 'No languages found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default LanguageList;